// Numbers from the API are integers: money in minor units at scale 4, rates in
// basis points, model cost in micro-dollars. These turn them into words.

export function money(minor: number | string | null | undefined, currency = 'USD', opts: { cents?: boolean } = {}): string {
  if (minor === null || minor === undefined || minor === '') return '–';
  const amount = Number(minor) / 10_000;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: opts.cents === false ? 0 : 2, minimumFractionDigits: opts.cents === false ? 0 : 2 }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export const bp = (v: number | null | undefined) => (v === null || v === undefined ? '–' : `${(v / 100).toFixed(v % 100 === 0 ? 0 : 1)}%`);
export const micros = (v: number | string | null | undefined) => (v === null || v === undefined ? '–' : `$${(Number(v) / 1_000_000).toFixed(Number(v) < 10_000_000 ? 4 : 2)}`);
export const count = (v: number | null | undefined) => (v === null || v === undefined ? '–' : v.toLocaleString());
export const ms = (v: number | null | undefined) => (v === null || v === undefined ? '–' : v < 1000 ? `${v} ms` : v < 120_000 ? `${(v / 1000).toFixed(1)} s` : `${Math.round(v / 60_000)} min`);
export const day = (iso: string | null | undefined) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '–');
// Keeps a minus sign, so a negative amount reaches the schema as negative, never flipped.
export const toMinor = (text: string) => Math.round(Number(text.replace(/[^0-9.-]/g, '')) * 10_000);
export const human = (s: string) => s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
// The systems an import comes from, as the Import page offers them and a contact's source names them.
export const IMPORT_SYSTEMS: Array<[string, string]> = [['csv', 'A spreadsheet'], ['referral_maker', 'Buffini Referral Maker'], ['other_crm', 'Another CRM']];
export const systemName = (s: string) => IMPORT_SYSTEMS.find(([k]) => k === s)?.[1] ?? human(s);
// A job an agent takes (its trigger class), as a workflow step offers it. A class with no entry reads as its name.
const JOBS: Record<string, string> = {
  lead_created: 'Answer a new lead', lead_email: 'Sort a lead that came in by email', deal_stalled: 'Get a stalled deal moving',
  deal_stage_changed: 'Follow up on a deal that moved stage', activity_logged: 'Follow up on a logged call, email, or meeting',
  reply: 'Draft a reply to a message', quote_request: 'Draft a quote', fraud_check: 'Check for signs of fraud',
  campaign_goal: 'Plan a campaign', content_request: 'Draft marketing content', sequence_step: 'Send the next email of a sequence',
  upload: 'Import an uploaded file', autonomy_proposed: "Review an agent's limits", benchmark_finding: 'Act on a benchmark finding',
  coaching_request: 'Coach an agent', dashboard_request: 'Draft a dashboard', workspace_request: "Change the workspace's setup",
  workflow_request: 'Draft a workflow', agent_request: 'Build a new agent', site_request: 'Change the website',
  browser_goal: 'Do a task on a website', onboarding_interview: 'Run the setup interview',
};
export const jobName = (c: string) => JOBS[c] ?? human(c);

export function formatCell(kind: string, v: unknown, currency = 'USD'): string {
  switch (kind) {
    case 'money': return money(v as number, currency, { cents: false });
    case 'bp': return bp(v as number);
    case 'micros': return micros(v as number);
    case 'count': return count(v as number);
    case 'ms': return ms(v as number);
    case 'date': return day(v as string);
    default: return v === null || v === undefined ? '–' : String(v);
  }
}
